import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Search, Check, Clock, FileSpreadsheet } from "lucide-react";
import { useAdminDietLeviesQuery } from "../api/upgradeApi";

const naira = (n: number | null | undefined) =>
  n == null ? "—" : "₦" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

const day = (s?: string | null) => (s ? String(s).slice(0, 10) : "—");

/**
 * Everyone this cycle has recommended, and where each of them has got to on
 * the cycle's levies.
 *
 * The cycle's main table is the work of DECIDING — who is assigned to whom,
 * who has been passed over. This is the work that follows: a recommendation
 * opens the applicant's bill, and from that moment the secretariat's question
 * stops being "has the committee decided" and becomes "who still owes".
 * Reading that off the main table meant checking a status column row by row.
 *
 * What each applicant owes is their GRADE's total, taken from the cycle's own
 * levies (the same figures the levies card shows), because the bill is set per
 * grade — the Fellows and the Members in one sitting do not owe the same.
 */
export default function RecommendedApplicantsCard({
  dietId,
  rows,
}: {
  dietId: string;
  /** The cycle's applications, as the cycle screen already has them. */
  rows: any[];
}) {
  const { data: leviesData } = useAdminDietLeviesQuery(dietId, { skip: !dietId });

  const [q, setQ] = useState("");

  /**
   * What this cycle bills one applicant: their grade's total, and — where the
   * grade runs upgrade routes — their ROUTE's total within it.
   *
   * A Probationer → Member upgrade runs the exams route (PCI) or the matured
   * route (MRI) and the two are priced differently, so one figure per grade was
   * right for at most one of them. Keyed "<grade>" and "<grade>:<route>", with
   * the route key taking precedence when the applicant has a route.
   */
  const billOf = useMemo(() => {
    const map: Record<string, number> = {};

    for (const t of leviesData?.totals ?? []) {
      const grade = String(t.to_category_id);
      map[grade] = Number(t.total ?? 0);

      for (const [route, rt] of Object.entries(t.route_totals ?? {})) {
        map[`${grade}:${route}`] = Number((rt as any)?.total ?? 0);
      }
    }

    return (app: any): number | undefined => {
      const grade = String(app?.to_category?.id ?? "");
      const route = String(app?.route ?? "").trim().toLowerCase();

      return (route ? map[`${grade}:${route}`] : undefined) ?? map[grade];
    };
  }, [leviesData]);

  /*
   * Recommended, and those already carried past it.
   *
   * `approved` is here too: paying the levies IS the approval, so an applicant
   * who has settled is approved and would otherwise vanish from the one list
   * that is meant to show the sitting's recommendations. Anything earlier than
   * recommended, and anything the committee declined, is not this list.
   */
  const recommended = useMemo(() => {
    const wanted = ["recommended", "approved"];
    const s = q.trim().toLowerCase();

    return rows
      .filter((a) => wanted.includes(String(a.status ?? "").toLowerCase()))
      .filter((a) =>
        !s ? true : `${a.name ?? ""} ${a.membership_no ?? ""}`.toLowerCase().includes(s),
      )
      .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));
  }, [rows, q]);

  const paidRows = recommended.filter((a) => !!a.induction_paid_at);
  const owing = recommended.filter((a) => !a.induction_paid_at);

  const owed = owing.reduce((sum, a) => sum + (billOf(a) ?? 0), 0);
  const collected = paidRows.reduce((sum, a) => sum + (billOf(a) ?? 0), 0);

  /** The list as a spreadsheet — what gets circulated and chased against. */
  const exportCsv = () => {
    const head = [
      "SN", "Name", "Membership No", "Upgrade", "Route", "Recommended On", "Levies", "Paid", "Paid On",
    ];

    const body = recommended.map((a, i) => [
      i + 1,
      a.name ?? "",
      a.membership_no ?? "",
      [a.from_grade, a.to_grade].filter(Boolean).join(" → "),
      // The route is what decides the figure in the next column, so a listing
      // without it cannot be checked.
      a.route ?? "",
      day(a.recommended_at),
      billOf(a) ?? "",
      a.induction_paid_at ? "Yes" : "No",
      day(a.induction_paid_at),
    ]);

    const csv = [head, ...body]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `recommended-${(leviesData?.diet?.title ?? "cycle").replace(/\s+/g, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <BadgeCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-black text-gray-900">
              Recommended applicants
              <span className="ml-2 text-sm font-bold text-gray-400">{recommended.length}</span>
            </h2>
            <p className="text-xs font-semibold text-gray-500">
              Recommended by this cycle, and whether each has settled its levies. Paying the levies
              is what approves the application.
            </p>
          </div>
        </div>

        {recommended.length > 0 ? (
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or no…"
                className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-gray-600 text-xs font-bold hover:bg-gray-50"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        ) : null}
      </div>

      {recommended.length === 0 ? (
        <p className="mt-5 text-sm text-gray-400">
          {q
            ? "Nobody in this cycle matches that search."
            : "Nobody has been recommended in this cycle yet. A committee member's recommendation — or an override — is what puts an applicant here."}
        </p>
      ) : (
        <>
          {/* Settled against outstanding. The figures are the levy totals for
              each applicant's grade, so they are what the cycle has billed, not
              what the gateway has confirmed line by line. */}
          <div className="mt-5 flex flex-wrap gap-2">
            <div className="px-3 py-2 rounded-2xl border border-emerald-100 bg-emerald-50/60">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600">
                Settled
              </div>
              <div className="text-sm font-black text-emerald-900 tabular-nums">
                {paidRows.length} · {naira(collected)}
              </div>
            </div>
            <div className="px-3 py-2 rounded-2xl border border-amber-100 bg-amber-50/60">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-600">
                Still to pay
              </div>
              <div className="text-sm font-black text-amber-900 tabular-nums">
                {owing.length} · {naira(owed)}
              </div>
            </div>
            {(leviesData?.data?.length ?? 0) === 0 ? (
              <div className="px-3 py-2 rounded-2xl border border-rose-200 bg-rose-50 text-[11px] font-bold text-rose-700 max-w-md">
                This cycle has no levies set, so these applicants have nothing to pay and cannot be
                invested. Set them in the levies card above.
              </div>
            ) : null}
          </div>

          <div className="mt-4 border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                  <th className="px-4 py-2 w-10 text-right">#</th>
                  <th className="px-4 py-2">Applicant</th>
                  <th className="px-4 py-2">Upgrade</th>
                  <th className="px-4 py-2">Recommended</th>
                  <th className="px-4 py-2 text-right">Levies</th>
                  <th className="px-4 py-2">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recommended.map((a, i) => {
                  const bill = billOf(a);
                  const route = String(a.route ?? "").trim();

                  return (
                    <tr key={a.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-400 font-semibold">
                        {i + 1}
                      </td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/admin/upgrade-applications/${a.id}`}
                          className="font-semibold text-gray-900 hover:text-indigo-600"
                        >
                          {a.name || "—"}
                        </Link>
                        <div className="text-[11px] text-gray-400">{a.membership_no || "—"}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {[a.from_grade, a.to_grade].filter(Boolean).join(" → ") || "—"}
                        {/* The route, where there is one — it is what decides
                            the figure in the Levies column, and two Members on
                            different routes owe different amounts. */}
                        {route ? (
                          <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                            {route} route
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 tabular-nums">
                        {day(a.recommended_at)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold tabular-nums text-gray-900">
                        {/* No figure where the cycle prices nothing for this
                            grade — a zero would read as "nothing to pay". */}
                        {bill == null ? (
                          <span className="text-[11px] font-semibold text-rose-600">
                            no levies for {a.to_grade || "this grade"}
                          </span>
                        ) : (
                          naira(bill)
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {a.induction_paid_at ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-bold">
                            <Check className="w-3 h-3" /> Paid {day(a.induction_paid_at)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[11px] font-bold">
                            <Clock className="w-3 h-3" /> Awaiting payment
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
